// ── AI Guidance JSONB type and helpers ──────────────────────────────────────

export interface AiGuidance {
  photo_evidence: string;
  speech_signals: string[];
  completion_criteria: string;
}

/** Empty form state for the three AI guidance fields */
export interface AiGuidanceForm {
  photo_evidence: string;
  speech_signals: string; // comma-separated in the UI
  completion_criteria: string;
}

export const EMPTY_AIGUIDANCE_FORM: AiGuidanceForm = {
  photo_evidence: '',
  speech_signals: '',
  completion_criteria: '',
};

/**
 * Convert stored AiGuidance (or null / legacy string) to form state.
 * Handles three cases:
 *   1. null / undefined → empty form
 *   2. AiGuidance object → populated form
 *   3. Legacy plain string → put it in photo_evidence for backwards compat
 */
export function aiguidanceToForm(
  stored: AiGuidance | string | null | undefined
): AiGuidanceForm {
  if (!stored) return { ...EMPTY_AIGUIDANCE_FORM };
  if (typeof stored === 'string') {
    // Legacy plain-text value — surface it in photo_evidence
    return {
      photo_evidence: stored,
      speech_signals: '',
      completion_criteria: '',
    };
  }
  return {
    photo_evidence: stored.photo_evidence ?? '',
    speech_signals: Array.isArray(stored.speech_signals)
      ? stored.speech_signals.join(', ')
      : '',
    completion_criteria: stored.completion_criteria ?? '',
  };
}

/**
 * Convert form state back to AiGuidance for the mutation payload.
 * Returns null if every field is blank/empty.
 */
export function formToAiguidance(form: AiGuidanceForm): AiGuidance | null {
  const photoEvidence = form.photo_evidence.trim();
  const speechSignals = form.speech_signals
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const completionCriteria = form.completion_criteria.trim();

  if (!photoEvidence && speechSignals.length === 0 && !completionCriteria) {
    return null;
  }

  return {
    photo_evidence: photoEvidence,
    speech_signals: speechSignals,
    completion_criteria: completionCriteria,
  };
}
